/**
 * Business-event transport: what the posting worker could not turn into a
 * journal, and the replay that gives it another go.
 *
 * Three contract facts the screen depends on:
 *
 *   • Replay PRESERVES the event's identity and payload. It never creates a
 *     replacement row, and posting stays idempotent because the journal's
 *     source reference is the event id — so replaying something that already
 *     posted cannot double-post it.
 *   • `attempts` counts committed attempts and replay never resets it. A
 *     number that keeps climbing is the honest record of a resolver that keeps
 *     failing.
 *   • Both the failure code and every rejection reason are closed, value-free
 *     vocabularies. They never echo payload values, so nothing here leaks what
 *     the event was about.
 */
import type { components } from "@erp/api-client";

import { apiFetch, type Page } from "@/lib/api/http";

type Schemas = components["schemas"];

export type FailedBusinessEvent = Schemas["FailedBusinessEvent"];
export type BusinessEventFailureCode = Schemas["BusinessEventFailureCode"];
export type BusinessEventReplayResult = Schemas["BusinessEventReplayResult"];

export interface FailedEventQuery {
  event_type?: string;
  occurred_from?: string;
  occurred_to?: string;
  cursor?: string;
  limit?: number;
  [key: string]: string | number | undefined;
}

export function listFailedBusinessEvents(
  query: FailedEventQuery = {},
  signal?: AbortSignal,
): Promise<Page<FailedBusinessEvent>> {
  return apiFetch<Page<FailedBusinessEvent>>("/business-events/failed", { query, signal });
}

/** At most 200 ids per call, and the ids must be distinct — the contract says
 *  so, and a duplicate would be a 422 rather than a second attempt. */
export function replayBusinessEvents(
  eventIds: readonly string[],
  idempotencyKey: string,
): Promise<BusinessEventReplayResult> {
  return apiFetch<BusinessEventReplayResult>("/business-events/replay", {
    method: "POST",
    body: { event_ids: [...new Set(eventIds)].slice(0, 200) },
    idempotencyKey,
  });
}

/**
 * Whether a failure is worth replaying as-is. A rule set that was wrong when
 * the event failed is still wrong on replay unless somebody changed it, so the
 * screen groups these rather than inviting a pointless retry.
 */
export function needsConfigurationChange(code: BusinessEventFailureCode): boolean {
  return (
    code === "UnmappedAccount" ||
    code === "InvalidRuleSet" ||
    code === "EffectiveRuleNotFound" ||
    code === "FXRateUnavailable"
  );
}
